import type { PerspectiveCamera } from "three";
import { Vector3 } from "three";
import type { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { latLonToLocal } from "./geo";
import { flyOffsetForStop } from "./hunt";
import { HUNT_STOPS, type HuntStop } from "./stops";

export type TourBeat = {
  stop: HuntStop;
  holdMs: number;
  flyMs: number;
  caption: string;
};

/** Demo path inspired by the 60s walkthrough — not every hunt stop. */
export const TOUR_BEATS: TourBeat[] = [
  {
    stop: HUNT_STOPS[0],
    flyMs: 1400,
    holdMs: 3200,
    caption: "PlaceStage — a stylized engagement twin of Old Québec.",
  },
  {
    stop: HUNT_STOPS[1],
    flyMs: 1600,
    holdMs: 2800,
    caption: "Owned place model — walls, streets, massing — not Google Earth.",
  },
  {
    stop: HUNT_STOPS[2],
    flyMs: 1700,
    holdMs: 2600,
    caption: "Layer one: Downtown Hunt — eight stops visitors finish in-browser.",
  },
  {
    stop: HUNT_STOPS[3],
    flyMs: 1500,
    holdMs: 2400,
    caption: "No app install. Sessions, completions, and CTA clicks for boards.",
  },
  {
    stop: HUNT_STOPS[6],
    flyMs: 1800,
    holdMs: 2600,
    caption: "Pilots ship in 21 days. Twin your district next.",
  },
  {
    stop: HUNT_STOPS[7],
    flyMs: 1600,
    holdMs: 3000,
    caption: "placestage.pages.dev — book a twenty-minute walkthrough.",
  },
];

type TourOpts = {
  camera: PerspectiveCamera;
  controls: OrbitControls;
  onCaption?: (text: string, index: number, total: number) => void;
  onBeat?: (stop: HuntStop) => void;
  onSync?: () => void;
  onDone?: () => void;
};

function easeOutCubic(u: number) {
  return 1 - (1 - u) ** 3;
}

function poseFor(stop: HuntStop) {
  const elevBoost =
    stop.id === "chateau-frontenac" ? 28 : Math.max(6, stop.height * 0.4);
  const target = latLonToLocal(stop.lat, stop.lon, elevBoost);
  const cam = target.clone().add(flyOffsetForStop(stop.id));
  return { target, cam };
}

export class CameraTour {
  private aborted = false;
  private running = false;
  private raf = 0;
  private opts: TourOpts;

  constructor(opts: TourOpts) {
    this.opts = opts;
  }

  get isRunning() {
    return this.running;
  }

  stop() {
    this.aborted = true;
    this.running = false;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  async play(beats: TourBeat[] = TOUR_BEATS): Promise<void> {
    this.stop();
    this.aborted = false;
    this.running = true;
    const { camera, controls } = this.opts;

    for (let i = 0; i < beats.length; i++) {
      if (this.aborted) break;
      const beat = beats[i];
      this.opts.onCaption?.(beat.caption, i, beats.length);
      this.opts.onBeat?.(beat.stop);

      const { target, cam } = poseFor(beat.stop);
      const fromTarget = controls.target.clone();
      const fromCam = camera.position.clone();
      await this.animatePose(fromTarget, fromCam, target, cam, beat.flyMs);
      if (this.aborted) break;
      await this.wait(beat.holdMs);
    }

    this.running = false;
    if (!this.aborted) this.opts.onDone?.();
  }

  private wait(ms: number) {
    return new Promise<void>((resolve) => {
      const start = performance.now();
      const tick = (now: number) => {
        if (this.aborted) {
          resolve();
          return;
        }
        if (now - start >= ms) {
          resolve();
          return;
        }
        this.raf = requestAnimationFrame(tick);
      };
      this.raf = requestAnimationFrame(tick);
    });
  }

  private animatePose(
    fromTarget: Vector3,
    fromCam: Vector3,
    toTarget: Vector3,
    toCam: Vector3,
    dur: number,
  ) {
    const { camera, controls } = this.opts;
    return new Promise<void>((resolve) => {
      const start = performance.now();
      const step = (now: number) => {
        if (this.aborted) {
          resolve();
          return;
        }
        const u = Math.min(1, (now - start) / Math.max(1, dur));
        const e = easeOutCubic(u);
        controls.target.lerpVectors(fromTarget, toTarget, e);
        camera.position.lerpVectors(fromCam, toCam, e);
        controls.update();
        this.opts.onSync?.();
        if (u < 1) this.raf = requestAnimationFrame(step);
        else resolve();
      };
      this.raf = requestAnimationFrame(step);
    });
  }
}
